import { useEffect, useRef, useState } from "react";

// ─── PaywallModal ─────────────────────────────────────────────────────────────
// Unified card: feature list once, then selectable plan chip cards.
//
// Props:
//   usedCount   — number of generations already used
//   limit       — the free tier limit
//   onSubscribe — (planKey) => void
//   onClose     — () => void

const PLAN_ORDER = ["monthly", "quarterly", "yearly"];

const PLAN_META = {
  monthly:   { label: "Monthly",  badge: null,           badgeKind: null },
  quarterly: { label: "3 Months", badge: "Most Popular", badgeKind: "primary" },
  yearly:    { label: "Yearly",   badge: "Best Value",   badgeKind: "subtle"  },
};

const FEATURES = [
  { icon: "♾️", text: "Unlimited AI listing generations" },
  { icon: "⚡", text: "1-tap copy to Facebook Marketplace" },
  { icon: "📦", text: "Single, bundle & bulk listing modes" },
  { icon: "🎨", text: "Custom tone, style & condition settings" },
  { icon: "📍", text: "Auto-include pickup location & price" },
];

function formatAmount(amount, currency) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: (currency || "usd").toUpperCase(),
      minimumFractionDigits: 2,
    }).format(amount / 100);
  } catch {
    return `$${(amount / 100).toFixed(2)}`;
  }
}

function billingPeriodText(plan) {
  const { interval, intervalCount } = plan;
  if (interval === "year") return "/ year";
  if (interval === "month" && intervalCount === 3) return "/ 3 months";
  return "/ month";
}

function monthsCovered(plan) {
  if (plan.interval === "year") return 12 * (plan.intervalCount || 1);
  if (plan.interval === "month") return plan.intervalCount || 1;
  return 1;
}

// Returns formatted per-month string for quarterly/yearly, null for monthly
function perMonthText(plan, currency) {
  const months = monthsCovered(plan);
  if (months <= 1) return null;
  return formatAmount(Math.round(plan.amount / months), currency) + "/mo";
}

function savingsPct(plan, monthlyPlan) {
  if (!monthlyPlan || plan.key === "monthly") return null;
  const basePerMonth = monthlyPlan.amount / (monthsCovered(monthlyPlan) || 1);
  const months = monthsCovered(plan);
  if (!months) return null;
  const pct = Math.round((1 - plan.amount / months / basePerMonth) * 100);
  return pct > 0 ? pct : null;
}

function headlineText(usedCount, limit) {
  const remaining = Math.max(0, limit - usedCount);
  if (remaining === 0) return `You've used all ${limit} free listings`;
  if (remaining === 1) return "1 free listing remaining";
  return `${remaining} free listings remaining`;
}

function descText(usedCount, limit) {
  const remaining = Math.max(0, limit - usedCount);
  if (remaining === 0)
    return "Upgrade to keep listing — unlock unlimited AI-generated listings and sell faster.";
  return "Upgrade anytime for unlimited AI-generated listings. No credit card required to try.";
}

export default function PaywallModal({ usedCount, limit, onSubscribe, onClose }) {
  const modalRef       = useRef(null);
  const closeButtonRef = useRef(null);

  const [plans,    setPlans]    = useState(null);
  const [selected, setSelected] = useState("quarterly");

  // ── Load plans from Stripe
  useEffect(() => {
    let cancelled = false;
    fetch("/stripe/plans")
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        const list = data?.success && Array.isArray(data.plans) ? data.plans : [];
        list.sort((a, b) => PLAN_ORDER.indexOf(a.key) - PLAN_ORDER.indexOf(b.key));
        setPlans(list);
        if (list.length > 0 && !list.some((p) => p.key === "quarterly")) {
          setSelected(list[0].key);
        }
      })
      .catch(() => { if (!cancelled) setPlans([]); });
    return () => { cancelled = true; };
  }, []);

  // ── Focus trap + ESC + body scroll lock
  useEffect(() => {
    const prev = document.activeElement;
    closeButtonRef.current?.focus();
    const handleKeyDown = (e) => {
      if (e.key === "Escape") { onClose(); return; }
      if (e.key === "Tab") {
        const focusable = modalRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0], last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
      prev?.focus();
    };
  }, [onClose]);

  const handleBackdropClick = (e) => { if (e.target === e.currentTarget) onClose(); };

  const monthlyPlan  = plans?.find((p) => p.key === "monthly") || null;
  const selectedPlan = plans?.find((p) => p.key === selected)  || null;

  return (
    <>
      <div className="pw-backdrop" onClick={handleBackdropClick} aria-hidden="true" />

      <div
        ref={modalRef}
        className="pw-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pw-title"
        aria-describedby="pw-desc"
      >
        <div className="pw-handle" aria-hidden="true" />
        <button ref={closeButtonRef} className="pw-close" onClick={onClose} aria-label="Close">✕</button>

        <div className="pw-content">
          {/* Icon */}
          <div className="pw-icon-wrap" aria-hidden="true">
            <span className="pw-icon">⚡</span>
          </div>

          {/* Headline */}
          <h2 className="pw-title" id="pw-title">{headlineText(usedCount, limit)}</h2>
          <p className="pw-desc" id="pw-desc">{descText(usedCount, limit)}</p>

          {/* ── Unified card */}
          <div className="pw-card">
            {/* Features — listed once */}
            <ul className="pw-features" aria-label="What's included">
              {FEATURES.map((f, i) => (
                <li key={i} className="pw-feature">
                  <span className="pw-feature-icon" aria-hidden="true">{f.icon}</span>
                  <span className="pw-feature-text">{f.text}</span>
                </li>
              ))}
            </ul>

            {/* Thick divider between features and plan chips */}
            <div className="pw-divider" aria-hidden="true" />

            {/* Plan chip cards */}
            {plans === null ? (
              <div className="pw-loading" aria-live="polite">Loading plans…</div>
            ) : plans.length > 0 ? (
              <div className="pw-chips" role="radiogroup" aria-label="Choose a billing period">
                {plans.map((p) => {
                  const meta  = PLAN_META[p.key] || { label: p.key, badge: null, badgeKind: null };
                  const pct   = savingsPct(p, monthlyPlan);
                  const perMo = perMonthText(p, p.currency);
                  const isOn  = p.key === selected;
                  return (
                    <button
                      key={p.key}
                      role="radio"
                      aria-checked={isOn}
                      className={`pw-chip${isOn ? " pw-chip--on" : ""}`}
                      onClick={() => setSelected(p.key)}
                    >
                      {/* Badge floated above */}
                      {meta.badge ? (
                        <span className={`pw-chip-badge pw-chip-badge--${meta.badgeKind}`}>
                          {meta.badge}
                        </span>
                      ) : (
                        <span className="pw-chip-badge-placeholder" aria-hidden="true" />
                      )}

                      <span className="pw-chip-label">{meta.label}</span>

                      <span className="pw-chip-price">
                        {formatAmount(p.amount, p.currency)}
                      </span>
                      <span className="pw-chip-period">{billingPeriodText(p)}</span>

                      {perMo ? (
                        <span className="pw-chip-permonth">{perMo}</span>
                      ) : (
                        <span className="pw-chip-permonth pw-chip-permonth--empty" aria-hidden="true" />
                      )}

                      {pct != null && (
                        <span className="pw-chip-save">Save {pct}%</span>
                      )}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>

          {/* CTA */}
          <button
            className="pw-cta"
            onClick={() => onSubscribe(selected)}
            aria-label={
              selectedPlan
                ? `Subscribe — ${formatAmount(selectedPlan.amount, selectedPlan.currency)} ${billingPeriodText(selectedPlan)}`
                : "Subscribe"
            }
          >
            <span>Start Unlimited Access</span>
            <span className="pw-cta-arrow" aria-hidden="true">→</span>
          </button>

          <p className="pw-trust">Cancel anytime · Secure checkout via Stripe</p>
          <button className="pw-dismiss" onClick={onClose}>Not now</button>
        </div>
      </div>

      <style>{`
        .pw-backdrop {
          position: fixed; inset: 0;
          background: rgba(26,26,26,0.45);
          backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);
          z-index: 100; animation: pw-fade 0.2s ease;
        }
        @keyframes pw-fade { from { opacity:0; } to { opacity:1; } }

        .pw-sheet {
          position: fixed; bottom: 0; left: 0; right: 0; z-index: 101;
          background: var(--color-bg-card);
          border-radius: 14px 14px 0 0;
          border-top: 0.5px solid var(--color-border);
          padding: 12px 20px calc(28px + env(safe-area-inset-bottom, 0px));
          max-width: 480px; margin: 0 auto;
          animation: pw-up 0.3s cubic-bezier(0.34,1.56,0.64,1);
          max-height: 92dvh; overflow-y: auto;
        }
        @keyframes pw-up { from { transform:translateY(100%); } to { transform:translateY(0); } }

        .pw-handle {
          width: 36px; height: 4px; border-radius: 9999px;
          background: var(--color-border-dashed); margin: 0 auto 18px;
        }

        .pw-close {
          position: absolute; top: 18px; right: 18px;
          width: 30px; height: 30px; border-radius: 50%;
          background: var(--color-bg-page); border: 0.5px solid var(--color-border);
          color: var(--color-text-hint); font-size: 13px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: background 0.15s; -webkit-tap-highlight-color: transparent;
        }
        .pw-close:active { transform: scale(0.9); }

        .pw-content { display: flex; flex-direction: column; align-items: center; }

        .pw-icon-wrap {
          width: 60px; height: 60px; border-radius: 14px;
          background: var(--color-primary-light);
          border: 0.5px solid var(--color-border);
          display: flex; align-items: center; justify-content: center;
          margin-bottom: 14px;
        }
        .pw-icon { font-size: 26px; }

        .pw-title {
          font-size: 18px; font-weight: 600;
          color: var(--color-text-heading); line-height: 1.3;
          margin-bottom: 7px; text-align: center;
        }
        .pw-desc {
          font-size: 13px; color: var(--color-text-secondary); line-height: 1.6;
          max-width: 300px; text-align: center; margin-bottom: 18px;
        }

        /* ── Unified card wrapper */
        .pw-card {
          width: 100%;
          border: 1px solid var(--color-border);
          border-radius: 14px;
          overflow: hidden;
          margin-bottom: 16px;
        }

        /* Features */
        .pw-features { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; }
        .pw-feature {
          display: flex; align-items: center; gap: 11px;
          padding: 10px 16px;
          border-bottom: 0.5px solid var(--color-border);
        }
        .pw-feature:last-child { border-bottom: none; }
        .pw-feature-icon { font-size: 15px; flex-shrink: 0; width: 20px; text-align: center; }
        .pw-feature-text { font-size: 13px; color: var(--color-text-secondary); }

        /* Divider between features and chips */
        .pw-divider {
          height: 7px;
          background: var(--color-bg-page);
          border-top: 0.5px solid var(--color-border);
          border-bottom: 0.5px solid var(--color-border);
        }

        .pw-loading { font-size: 13px; color: var(--color-text-hint); padding: 20px 16px; text-align: center; }

        /* ── Plan chip cards grid */
        .pw-chips {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0;
          padding: 12px 12px 14px;
          background: var(--color-bg-card);
          gap: 8px;
        }

        .pw-chip {
          position: relative;
          display: flex; flex-direction: column; align-items: center;
          padding: 10px 6px 10px;
          background: var(--color-bg-page);
          border: 1.5px solid var(--color-border);
          border-radius: 10px;
          font-family: inherit; cursor: pointer;
          transition: border-color 0.14s, background 0.14s;
          -webkit-tap-highlight-color: transparent;
          gap: 2px;
        }
        .pw-chip:active { opacity: 0.85; }
        .pw-chip--on {
          border-color: var(--color-primary);
          background: var(--color-primary-light);
        }

        /* Badge row — always same height so cards align */
        .pw-chip-badge {
          font-size: 9px; font-weight: 700; letter-spacing: 0.2px;
          padding: 2px 6px; border-radius: 9999px; white-space: nowrap;
          margin-bottom: 4px; line-height: 1.3;
        }
        .pw-chip-badge--primary {
          background: var(--color-primary); color: #EAF3DE;
        }
        .pw-chip-badge--subtle {
          background: var(--color-primary-light); color: var(--color-primary);
          border: 0.5px solid var(--color-primary);
        }
        .pw-chip-badge-placeholder {
          display: block; height: 18px; margin-bottom: 4px;
        }

        .pw-chip-label {
          font-size: 11px; font-weight: 600;
          color: var(--color-text-secondary);
          text-transform: uppercase; letter-spacing: 0.5px;
          margin-bottom: 2px;
        }
        .pw-chip--on .pw-chip-label { color: var(--color-primary); }

        .pw-chip-price {
          font-size: 15px; font-weight: 700;
          color: var(--color-text-heading); letter-spacing: -0.4px; line-height: 1.1;
        }
        .pw-chip-period {
          font-size: 10px; color: var(--color-text-hint); line-height: 1.2;
        }

        /* Per-month equivalent */
        .pw-chip-permonth {
          font-size: 10px; font-weight: 500;
          color: var(--color-primary);
          margin-top: 3px; line-height: 1.2;
        }
        .pw-chip-permonth--empty { display: block; height: 14px; margin-top: 3px; }

        /* Savings pill — sits below per-month */
        .pw-chip-save {
          font-size: 9px; font-weight: 700;
          background: var(--color-primary-light); color: var(--color-primary);
          padding: 1px 5px; border-radius: 9999px;
          margin-top: 3px; white-space: nowrap;
        }

        /* ── CTA */
        .pw-cta {
          width: 100%; height: 48px; padding: 0 20px;
          background: var(--color-primary); color: #EAF3DE; border: none;
          border-radius: var(--radius-md); font-family: inherit;
          font-size: 15px; font-weight: 600; cursor: pointer;
          display: flex; align-items: center; justify-content: center; gap: 8px;
          transition: background 0.15s, transform 0.1s;
          -webkit-tap-highlight-color: transparent; margin-bottom: 10px;
        }
        .pw-cta:hover { background: var(--color-primary-hover); }
        .pw-cta:active { transform: scale(0.985); }
        .pw-cta-arrow { font-size: 16px; transition: transform 0.15s; }
        .pw-cta:hover .pw-cta-arrow { transform: translateX(3px); }

        .pw-trust { font-size: 12px; color: var(--color-text-hint); margin-bottom: 14px; text-align: center; }

        .pw-dismiss {
          background: none; border: none; color: var(--color-text-hint);
          font-family: inherit; font-size: 13px; cursor: pointer;
          padding: 8px 16px; border-radius: var(--radius-sm);
          transition: color 0.15s; -webkit-tap-highlight-color: transparent;
        }
        .pw-dismiss:hover { color: var(--color-text-secondary); }
      `}</style>
    </>
  );
}
